import { calculateSchedule } from "../shared/scheduling";

export * from "../shared/scheduling";

export const calculateProjectedEndTime = (
  tasks: any[], // TODO: Fix type
  taskStates: Record<string, any>,
  runStartTime: number,
): number => {
  const now = Date.now();
  const memo = new Map<string, number>();
  const visiting = new Set<string>();

  const getTaskExpectedEndTime = (taskId: string): number => {
    if (memo.has(taskId)) return memo.get(taskId)!;
    if (visiting.has(taskId)) return now;

    visiting.add(taskId);

    const task = tasks.find((t) => t.id === taskId);
    if (!task) {
      visiting.delete(taskId);
      return now;
    }

    const state = taskStates[taskId];

    // Case 1: Completed/Skipped - use actual end time
    if (state && (state.status === "COMPLETED" || state.status === "SKIPPED")) {
      const end = state.endTime || now;
      visiting.delete(taskId);
      memo.set(taskId, end);
      return end;
    }

    // Case 2: Running/Waiting - use max(now, start + duration)
    if (
      state &&
      (state.status === "RUNNING" || state.status === "WAITING_FOR_USER")
    ) {
      const startTime = state.startTime || now;
      let durationMs = (task.durationMinutes || 0) * 60 * 1000;

      if (task.durationStrategy === "FIXED_END_TIME" && task.fixedEndTime) {
        const startDate = new Date(runStartTime);
        const targetDate = new Date(startDate);
        targetDate.setDate(
          startDate.getDate() + (task.fixedEndTime.daysOffset || 0),
        );
        const [hours, minutes] = task.fixedEndTime.time.split(":").map(Number);
        targetDate.setHours(hours, minutes, 0, 0);
        durationMs = Math.max(0, targetDate.getTime() - startTime);
      }

      const expected = Math.max(now, startTime + durationMs);
      visiting.delete(taskId);
      memo.set(taskId, expected);
      return expected;
    }

    // Case 3: Pending/Blocked/Failed - wait for dependencies
    let start = runStartTime;
    if (task.dependencyIds && task.dependencyIds.length > 0) {
      const depEnds = task.dependencyIds.map(getTaskExpectedEndTime);
      start = Math.max(runStartTime, ...depEnds);
    }

    // If dependencies are finished but this hasn't started, the earliest it can start is 'now'
    start = Math.max(start, now);

    let durationMs =
      (task.type === "SECTION" ? 0 : task.durationMinutes || 0) *
      60 *
      1000;

    if (
      task.durationStrategy === "FIXED_END_TIME" &&
      task.fixedEndTime &&
      task.type !== "SECTION"
    ) {
      const startDate = new Date(runStartTime);
      const targetDate = new Date(startDate);
      targetDate.setDate(
        startDate.getDate() + (task.fixedEndTime.daysOffset || 0),
      );
      const [hours, minutes] = task.fixedEndTime.time.split(":").map(Number);
      targetDate.setHours(hours, minutes, 0, 0);
      durationMs = Math.max(0, targetDate.getTime() - start);
    }

    const expected = start + durationMs;
    visiting.delete(taskId);
    memo.set(taskId, expected);
    return expected;
  };

  let maxEnd = runStartTime;
  tasks.forEach((t) => {
    maxEnd = Math.max(maxEnd, getTaskExpectedEndTime(t.id));
  });

  return maxEnd;
};

export const calculateProgress = (
  tasks: any[], // TODO: Fix type
  taskStates: Record<string, any>,
  runStartTime: number,
  currentTime: number = Date.now(),
): number => {
  const now = currentTime;
  const schedule = calculateSchedule(tasks, runStartTime);
  let projectDuration = 0;
  Object.values(schedule).forEach((s: any) => {
    projectDuration = Math.max(projectDuration, s.end);
  });

  if (projectDuration === 0) {
    const allDone = tasks.every(
      (t) =>
        t.type === "SECTION" ||
        (taskStates[t.id] &&
          (taskStates[t.id].status === "COMPLETED" ||
            taskStates[t.id].status === "SKIPPED")),
    );
    return allDone ? 100 : 0;
  }

  // To calculate progress relative to the project duration (Timeline scale):
  // We find the 'latest' completed/interpolated time point in the project.
  let latestTimePoint = 0;
  let allDone = true;

  tasks.forEach((task) => {
    if (task.type === "SECTION") return;
    const sched = schedule[task.id];
    if (!sched) return;

    const state = taskStates[task.id];
    if (
      state &&
      (state.status === "COMPLETED" ||
        state.status === "SKIPPED")
    ) {
      latestTimePoint = Math.max(latestTimePoint, sched.end);
    } else {
      allDone = false;
      if (
        state &&
        (state.status === "RUNNING" ||
          state.status === "WAITING_FOR_USER")
      ) {
        const startTime = state.startTime || now;
        const elapsedMinutes = (now - startTime) / 1000 / 60;

        let plannedDuration = task.durationMinutes || 0;
        if (task.durationStrategy === "FIXED_END_TIME" && task.fixedEndTime) {
          plannedDuration = sched.end - sched.start;
        }

        if (plannedDuration > 0) {
          const ratio = Math.min(
            1,
            Math.max(0, elapsedMinutes / plannedDuration),
          );
          const interpolatedTime = sched.start + plannedDuration * ratio;
          latestTimePoint = Math.max(latestTimePoint, interpolatedTime);
        } else {
          latestTimePoint = Math.max(latestTimePoint, sched.start);
        }
      }
    }
  });

  if (allDone) return 100;

  const pct = (latestTimePoint / projectDuration) * 100;
  return Math.min(Math.max(0, pct), 99.9);
};
